import { INumberFormatter, NUMBER_FORMATTER_TOKEN } from '@cgs/common';
import { SpinResponse } from '@cgs/data-access';
import {
  CollapseGameStateMachine,
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  GAME_WIN_COMPONENT_TOKEN,
  GameWinComponent,
  IGameStateMachineProvider,
  RESOURCES_COMPONENT_TOKEN,
  ResourcesComponent,
} from '@cgs/machines';
import { TRIFECTA_LAUNCH_PARAMS_TOKEN, TrifectaLaunchParamsDto } from '@cgs/provider-trifecta';
import {
  Container,
  EmptyAction,
  FunctionAction,
  IntervalAction,
  SceneObject,
  SequenceAction,
  TextSceneObject,
} from '@cgs/wge';

export const SANTA_COLLAPSE_LOW_WIN_COMPONENT_TOKEN = Symbol('SantaCollapseLowWinComponent');

const enum SceneNames {
  LowWin = 'lowWIn',
  LowWinText = 'lowWinTxt',
}

export class SantaCollapseLowWinComponent {
  private readonly lowWinScene: SceneObject;
  private readonly lowWinTextNode: TextSceneObject;
  private readonly gameWinComponent: GameWinComponent;
  private readonly gameStateMachine: CollapseGameStateMachine<SpinResponse>;

  private readonly currencyDecimals: number;
  private readonly currency: string;

  private readonly numberFormatter: INumberFormatter;

  constructor(container: Container) {
    this.gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine as CollapseGameStateMachine<SpinResponse>;

    this.gameStateMachine.animation.addLazyAnimationToBegin(() => this.showLowWinAnimationAction());

    this.gameWinComponent = container.resolve<GameWinComponent>(GAME_WIN_COMPONENT_TOKEN);

    const launchParams = container.resolve<TrifectaLaunchParamsDto>(TRIFECTA_LAUNCH_PARAMS_TOKEN);

    this.currencyDecimals = launchParams.currencyDecimals;
    this.currency = launchParams.currencyId;

    const root = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN).slot;

    const lowWinScene = root.findById(SceneNames.LowWin);

    this.numberFormatter = container.resolve<INumberFormatter>(NUMBER_FORMATTER_TOKEN);

    if (!lowWinScene) {
      throw new Error('Low win scene not found');
    }

    this.lowWinScene = lowWinScene;

    const lowWinTextNode = lowWinScene.findById<TextSceneObject>(SceneNames.LowWinText);

    if (!lowWinTextNode) {
      throw new Error('Low win text node not found');
    }
    this.lowWinTextNode = lowWinTextNode;
  }

  private showLowWinAnimationAction(): IntervalAction {
    const totalWin = this.gameStateMachine.curResponse.totalWin;

    if (totalWin === 0) {
      return new EmptyAction();
    }

    const totalWinString = this.numberFormatter.format(totalWin, this.currencyDecimals);

    return new SequenceAction([
      new EmptyAction().withDuration(this.lowWinScene.stateMachine?.getStateDuration('hide') || 0),
      new FunctionAction(() => {
        this.lowWinTextNode.text = `${this.currency} ${totalWinString}`;
        this.lowWinScene.stateMachine?.switchToState('show');

        if (this.gameStateMachine.curResponse.freeSpinsInfo) {
          return;
        }
        this.gameWinComponent.addWin(totalWin);
      }),
      new EmptyAction().withDuration(this.lowWinScene.stateMachine?.getStateDuration('show') || 0),
    ]);
  }
}
